import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiUsers, FiEdit, FiPlus, FiCheck, FiX, FiLogOut, FiHome, FiSave, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { auth, analytics, logEvent, db } from '../firebase';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, updateDoc, deleteDoc, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Types
interface Activiteit {
  id: string;
  titel: string;
  datum: string;
  tijd: string;
  locatie: string;
  beschrijving: string;
  partnerToegestaan: boolean;
  aangemaaktDoor: string;
  aangemaaktOp: Date;
  laatstGewijzigd: Date;
}

interface Aanwezigheid {
  id: string;
  activiteitId: string;
  lidEmail: string;
  lidNaam: string;
  aanwezig: boolean;
  partnerAanwezig: boolean;
  partnerNaam?: string;
  opmerkingen?: string;
  aangemeldOp: Date;
}

interface Lid {
  id: string;
  email: string;
  naam: string;
  rollen: string[];
}

const Prac: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activiteiten, setActiviteiten] = useState<Activiteit[]>([]);
  const [aanwezigheden, setAanwezigheden] = useState<Aanwezigheid[]>([]);
  const [leden, setLeden] = useState<Lid[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActiviteit, setEditingActiviteit] = useState<Activiteit | null>(null);
  const [formData, setFormData] = useState({
    titel: '',
    datum: '',
    tijd: '',
    locatie: '',
    beschrijving: '',
    partnerToegestaan: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isPracLid, setIsPracLid] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'not-registered' | 'attending'>('all');
  const [showAanwezigheidDetails, setShowAanwezigheidDetails] = useState<string | null>(null);
  const navigate = useNavigate();

  // Focus op eerste veld wanneer edit formulier wordt geopend
  useEffect(() => {
    if (editingActiviteit) {
      // Scroll naar het formulier
      const formElement = document.querySelector('.edit-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Focus op het eerste input veld na een korte delay
      setTimeout(() => {
        const firstInput = document.querySelector('.edit-form input[type="text"]') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
          // Selecteer alle tekst in het veld voor gemakkelijke bewerking
          firstInput.select();
        }
      }, 200);
    }
  }, [editingActiviteit]);

  // Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      
      if (!user) {
        navigate('/leden-login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Google Analytics page view
  useEffect(() => {
    if (analytics && user) {
      logEvent(analytics, 'page_view', {
        page_title: 'PRAC Activiteiten',
        page_location: window.location.href
      });
    }
  }, [user]);

  // Leden en rollen laden
  useEffect(() => {
    if (user) {
      loadLeden();
    }
  }, [user]);

  // Check PRAC rol nadat leden zijn geladen
  useEffect(() => {
    if (user && leden.length > 0) {
      checkPracRol();
    }
  }, [user, leden]);

  // Activiteiten laden
  useEffect(() => {
    if (user) {
      loadActiviteiten();
      loadAanwezigheden();
    }
  }, [user]);

  // Update carousel activiteiten wanneer admin inlogt
  useEffect(() => {
    if (user && user.email === 'p@de-boer.net' && activiteiten.length > 0) {
      // Update carousel activiteiten op de homepage
      const updateCarouselActiviteiten = (window as any).updateCarouselActiviteiten;
      if (updateCarouselActiviteiten && typeof updateCarouselActiviteiten === 'function') {
        const toekomstigeActiviteiten = activiteiten
          .filter(activiteit => {
            const activiteitDatum = new Date(activiteit.datum);
            const vandaag = new Date();
            vandaag.setHours(0, 0, 0, 0);
            return activiteitDatum >= vandaag;
          })
          .sort((a, b) => new Date(a.datum).getTime() - new Date(b.datum).getTime())
          .slice(0, 2);
        
        updateCarouselActiviteiten(toekomstigeActiviteiten);
      }
    }
  }, [user, activiteiten]);

  const loadLeden = async () => {
    try {
      const ledenRef = collection(db, 'leden');
      const snapshot = await getDocs(ledenRef);
      const ledenData: Lid[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        ledenData.push({
          id: doc.id,
          email: data.email,
          naam: data.naam,
          rollen: data.rollen || ['Standaardlid']
        });
      });
      
      setLeden(ledenData);
    } catch (err: any) {
      console.error('Error loading leden:', err);
    }
  };

  const checkPracRol = () => {
    if (user) {
      const currentLid = leden.find(lid => lid.email === user.email);
      const hasPracRole = currentLid?.rollen.includes('PRAC') || false;
      setIsPracLid(hasPracRole);
      console.log('User:', user.email, 'PRAC role:', hasPracRole, 'Roles:', currentLid?.rollen);
    }
  };

  const loadActiviteiten = async () => {
    try {
      const activiteitenRef = collection(db, 'activiteiten');
      const snapshot = await getDocs(activiteitenRef);
      const activiteitenData: Activiteit[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        activiteitenData.push({
          id: doc.id,
          titel: data.titel,
          datum: data.datum,
          tijd: data.tijd,
          locatie: data.locatie,
          beschrijving: data.beschrijving,
          partnerToegestaan: data.partnerToegestaan || false,
          aangemaaktDoor: data.aangemaaktDoor,
          aangemaaktOp: data.aangemaaktOp?.toDate() || new Date(),
          laatstGewijzigd: data.laatstGewijzigd?.toDate() || new Date()
        });
      });
      
      // Sorteer op datum
      activiteitenData.sort((a, b) => new Date(a.datum).getTime() - new Date(b.datum).getTime());
      setActiviteiten(activiteitenData);
    } catch (err: any) {
      console.error('Error loading activiteiten:', err);
      setError('Fout bij het laden van activiteiten.');
    }
  };

  const loadAanwezigheden = async () => {
    try {
      const aanwezighedenRef = collection(db, 'aanwezigheden');
      const snapshot = await getDocs(aanwezighedenRef);
      const aanwezighedenData: Aanwezigheid[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        aanwezighedenData.push({
          id: doc.id,
          activiteitId: data.activiteitId,
          lidEmail: data.lidEmail,
          lidNaam: data.lidNaam,
          aanwezig: data.aanwezig,
          partnerAanwezig: data.partnerAanwezig || false,
          partnerNaam: data.partnerNaam,
          opmerkingen: data.opmerkingen,
          aangemeldOp: data.aangemeldOp?.toDate() || new Date()
        });
      });
      
      setAanwezigheden(aanwezighedenData);
    } catch (err: any) {
      console.error('Error loading aanwezigheden:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      
      if (analytics) {
        logEvent(analytics, 'logout');
      }
      
      navigate('/');
    } catch (err: any) {
      console.error('Logout error:', err);
    }
  };

  const handleAddActiviteit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.titel || !formData.datum || !formData.tijd || !formData.locatie) {
      setError('Vul alle verplichte velden in.');
      return;
    }

    try {
      const activiteitData = {
        titel: formData.titel,
        datum: formData.datum,
        tijd: formData.tijd,
        locatie: formData.locatie,
        beschrijving: formData.beschrijving,
        partnerToegestaan: formData.partnerToegestaan,
        aangemaaktDoor: user?.email || '',
        aangemaaktOp: serverTimestamp(),
        laatstGewijzigd: serverTimestamp()
      };

      await addDoc(collection(db, 'activiteiten'), activiteitData);

      setSuccess('Activiteit succesvol toegevoegd!');
      setFormData({
        titel: '',
        datum: '',
        tijd: '',
        locatie: '',
        beschrijving: '',
        partnerToegestaan: false
      });
      setShowAddForm(false);
      loadActiviteiten();

      if (analytics) {
        logEvent(analytics, 'activiteit_toegevoegd', {
          titel: formData.titel
        });
      }
    } catch (err: any) {
      console.error('Error adding activiteit:', err);
      setError('Fout bij het toevoegen van activiteit.');
    }
  };

  const handleEditActiviteit = async (activiteit: Activiteit) => {
    setEditingActiviteit(activiteit);
    setFormData({
      titel: activiteit.titel,
      datum: activiteit.datum,
      tijd: activiteit.tijd,
      locatie: activiteit.locatie,
      beschrijving: activiteit.beschrijving,
      partnerToegestaan: activiteit.partnerToegestaan
    });
  };

  const handleUpdateActiviteit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingActiviteit) return;

    try {
      const activiteitRef = doc(db, 'activiteiten', editingActiviteit.id);
      await updateDoc(activiteitRef, {
        titel: formData.titel,
        datum: formData.datum,
        tijd: formData.tijd,
        locatie: formData.locatie,
        beschrijving: formData.beschrijving,
        partnerToegestaan: formData.partnerToegestaan,
        laatstGewijzigd: serverTimestamp()
      });

      setSuccess('Activiteit succesvol bijgewerkt!');
      setEditingActiviteit(null);
      setFormData({
        titel: '',
        datum: '',
        tijd: '',
        locatie: '',
        beschrijving: '',
        partnerToegestaan: false
      });
      loadActiviteiten();

      if (analytics) {
        logEvent(analytics, 'activiteit_bijgewerkt', {
          titel: formData.titel
        });
      }
    } catch (err: any) {
      console.error('Error updating activiteit:', err);
      setError('Fout bij het bijwerken van activiteit.');
    }
  };

  const handleDeleteActiviteit = async (activiteitId: string, titel: string) => {
    // SweetAlert2 bevestiging
    const result = await Swal.fire({
      title: 'Activiteit verwijderen?',
      text: `Weet u zeker dat u de activiteit "${titel}" wilt verwijderen?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, verwijder!',
      cancelButtonText: 'Annuleren',
      background: '#1e293b',
      color: '#ffffff',
      customClass: {
        popup: 'dark:bg-gray-800',
        title: 'text-white'
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'activiteiten', activiteitId));
        
        // Succes melding
        Swal.fire({
          title: 'Verwijderd!',
          text: 'De activiteit is succesvol verwijderd.',
          icon: 'success',
          background: '#1e293b',
          color: '#ffffff',
          confirmButtonColor: '#10b981'
        });
        
        setSuccess('Activiteit succesvol verwijderd!');
        loadActiviteiten();

        if (analytics) {
          logEvent(analytics, 'activiteit_verwijderd', {
            titel: titel
          });
        }
      } catch (err: any) {
        console.error('Error deleting activiteit:', err);
        
        // Fout melding
        Swal.fire({
          title: 'Fout!',
          text: 'Er is een fout opgetreden bij het verwijderen van de activiteit.',
          icon: 'error',
          background: '#1e293b',
          color: '#ffffff',
          confirmButtonColor: '#ef4444'
        });
        
        setError('Fout bij het verwijderen van activiteit.');
      }
    }
  };



  const handleAanwezigheid = async (activiteitId: string, aanwezigheidType: 'alleen' | 'met-partner' | 'niet') => {
    if (!user) return;

    try {
      const currentLid = leden.find(lid => lid.email === user.email);
      if (!currentLid) {
        setError('Lid niet gevonden.');
        return;
      }

      const aanwezig = aanwezigheidType !== 'niet';
      const partnerAanwezig = aanwezigheidType === 'met-partner';

      // Check of er al een aanwezigheid is geregistreerd
      const existingAanwezigheid = aanwezigheden.find(
        a => a.activiteitId === activiteitId && a.lidEmail === user.email
      );

      if (existingAanwezigheid) {
        // Update bestaande aanwezigheid
        const aanwezigheidRef = doc(db, 'aanwezigheden', existingAanwezigheid.id);
        await updateDoc(aanwezigheidRef, {
          aanwezig,
          partnerAanwezig,
          partnerNaam: partnerAanwezig ? 'Partner' : '',
          laatstGewijzigd: serverTimestamp()
        });
      } else {
        // Nieuwe aanwezigheid toevoegen
        await addDoc(collection(db, 'aanwezigheden'), {
          activiteitId,
          lidEmail: user.email,
          lidNaam: currentLid.naam,
          aanwezig,
          partnerAanwezig,
          partnerNaam: partnerAanwezig ? 'Partner' : '',
          aangemeldOp: serverTimestamp()
        });
      }

      let successMessage = '';
      switch (aanwezigheidType) {
        case 'alleen':
          successMessage = 'Aanwezigheid geregistreerd: Ja, ik kom alleen';
          break;
        case 'met-partner':
          successMessage = 'Aanwezigheid geregistreerd: Ja, ik kom met partner';
          break;
        case 'niet':
          successMessage = 'Aanwezigheid geregistreerd: Niet aanwezig';
          break;
      }

      setSuccess(successMessage);
      loadAanwezigheden();

      if (analytics) {
        logEvent(analytics, 'aanwezigheid_geregistreerd', {
          activiteit_id: activiteitId,
          aanwezigheid_type: aanwezigheidType
        });
      }
    } catch (err: any) {
      console.error('Error registering aanwezigheid:', err);
      setError('Fout bij het registreren van aanwezigheid.');
    }
  };

  const getAanwezigheidStatus = (activiteitId: string) => {
    const aanwezigheid = aanwezigheden.find(
      a => a.activiteitId === activiteitId && a.lidEmail === user?.email
    );
    
    if (!aanwezigheid) return null;
    
    if (!aanwezigheid.aanwezig) return 'niet';
    if (aanwezigheid.partnerAanwezig) return 'met-partner';
    return 'alleen';
  };

  // Filter activiteiten based on current filter
  const filteredActiviteiten = activiteiten.filter(activiteit => {
    // Filter eerst op datum - toon alleen activiteiten in de toekomst
    const activiteitDatum = new Date(activiteit.datum);
    const vandaag = new Date();
    vandaag.setHours(0, 0, 0, 0); // Reset tijd naar 00:00:00 voor juiste vergelijking
    
    if (activiteitDatum < vandaag) {
      return false; // Activiteit in het verleden, niet tonen
    }
    
    const status = getAanwezigheidStatus(activiteit.id);
    
    switch (activeFilter) {
      case 'not-registered':
        return !status; // Nog niet geregistreerd
      case 'attending':
        return status === 'alleen' || status === 'met-partner'; // Ja ik kom (alleen of met partner)
      case 'all':
      default:
        return true; // Toon alles
    }
  });

  // Bereken aanwezigheid details voor een activiteit
  const getAanwezigheidDetails = (activiteitId: string) => {
    const aanwezighedenVoorActiviteit = aanwezigheden.filter(a => a.activiteitId === activiteitId);
    const aanwezigLeden = aanwezighedenVoorActiviteit.filter(a => a.aanwezig && !a.partnerAanwezig);
    const aanwezigMetPartner = aanwezighedenVoorActiviteit.filter(a => a.aanwezig && a.partnerAanwezig);
    const nietAanwezig = aanwezighedenVoorActiviteit.filter(a => !a.aanwezig);
    
    const totaalLeden = aanwezigLeden.length;
    const totaalPartners = aanwezigMetPartner.length;
    const totaalPersonen = totaalLeden + totaalPartners;
    
    return {
      aanwezigLeden,
      aanwezigMetPartner,
      nietAanwezig,
      totaalLeden,
      totaalPartners,
      totaalPersonen
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <FiCalendar className="text-xl text-indigo-900" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">PRAC Activiteiten</h1>
                <p className="text-gray-300 text-sm">Public Relations en Communicatie</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right text-white">
                <p className="text-sm text-gray-300">Welkom,</p>
                <p className="font-medium">{user.email}</p>
                {isPracLid && (
                  <span className="text-xs bg-yellow-500 text-yellow-900 px-2 py-1 rounded-full">
                    PRAC Lid
                  </span>
                )}
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => navigate('/leden-dashboard')}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  title="Terug naar dashboard"
                >
                  <FiHome className="text-lg" />
                </button>
                
                <button
                  onClick={handleLogout}
                  className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <FiLogOut className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Error/Success Messages */}
          {error && (
            <motion.div 
              variants={fadeIn}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center"
            >
              <FiX className="mr-2 flex-shrink-0" />
              <p>{error}</p>
            </motion.div>
          )}
          
          {success && (
            <motion.div 
              variants={fadeIn}
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
            >
              <FiCheck className="mr-2 flex-shrink-0" />
              <p>{success}</p>
            </motion.div>
          )}



          {/* Add New Activiteit Button (alleen voor PRAC leden) */}
          {isPracLid && (
            <motion.div variants={fadeIn} className="mb-8">
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 flex items-center"
              >
                <FiPlus className="mr-2" />
                Nieuwe Activiteit Toevoegen
              </button>
            </motion.div>
          )}

          {/* Add/Edit Form */}
          {(showAddForm || editingActiviteit) && (
            <motion.div 
              variants={fadeIn}
              className="edit-form bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingActiviteit ? 'Activiteit Bewerken' : 'Nieuwe Activiteit Toevoegen'}
              </h2>
              
              <form onSubmit={editingActiviteit ? handleUpdateActiviteit : handleAddActiviteit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-medium">Titel *</label>
                    <input
                      type="text"
                      value={formData.titel}
                      onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="Activiteit titel"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white mb-2 font-medium">Datum *</label>
                    <input
                      type="date"
                      value={formData.datum}
                      onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white mb-2 font-medium">Tijd *</label>
                    <input
                      type="time"
                      value={formData.tijd}
                      onChange={(e) => setFormData({ ...formData, tijd: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white mb-2 font-medium">Locatie *</label>
                    <input
                      type="text"
                      value={formData.locatie}
                      onChange={(e) => setFormData({ ...formData, locatie: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="Locatie"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">Beschrijving</label>
                  <textarea
                    value={formData.beschrijving}
                    onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    placeholder="Beschrijving van de activiteit..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="partnerToegestaan"
                    checked={formData.partnerToegestaan}
                    onChange={(e) => setFormData({ ...formData, partnerToegestaan: e.target.checked })}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="partnerToegestaan" className="ml-2 text-white">
                    Partners toegestaan (P)
                  </label>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <FiSave className="inline mr-2" />
                    {editingActiviteit ? 'Bijwerken' : 'Toevoegen'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingActiviteit(null);
                      setFormData({
                        titel: '',
                        datum: '',
                        tijd: '',
                        locatie: '',
                        beschrijving: '',
                        partnerToegestaan: false
                      });
                    }}
                    className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                  >
                    Annuleren
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Filter Knoppen */}
          <motion.div variants={fadeIn} className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Komende Activiteiten</h2>
            
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeFilter === 'all'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                Toon alles
              </button>
              
              <button
                onClick={() => setActiveFilter('not-registered')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeFilter === 'not-registered'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                Nog niet geregistreerd
              </button>
              
              <button
                onClick={() => setActiveFilter('attending')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeFilter === 'attending'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                Ja ik kom (alleen of met partner)
              </button>
            </div>
            
            {filteredActiviteiten.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20 text-center">
                <FiCalendar className="text-4xl mx-auto mb-4 text-gray-400" />
                <p className="text-gray-300">
                  {activiteiten.length === 0 
                    ? 'Nog geen activiteiten gepland' 
                    : activeFilter === 'not-registered'
                    ? 'Geen activiteiten waar je nog niet voor geregistreerd bent'
                    : activeFilter === 'attending'
                    ? 'Geen activiteiten waar je voor aangemeld bent'
                    : 'Geen activiteiten gevonden'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredActiviteiten.map((activiteit) => {
                  const aanwezigheid = getAanwezigheidStatus(activiteit.id);
                  const aanwezighedenVoorActiviteit = aanwezigheden.filter(a => a.activiteitId === activiteit.id);
                  const gereageerdCount = aanwezighedenVoorActiviteit.length; // Aantal mensen die gereageerd hebben
                  const totaalLedenCount = leden.length; // Totaal aantal leden
                  
                  return (
                    <motion.div
                      key={activiteit.id}
                      variants={fadeIn}
                      className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden"
                    >
                      {/* Titel sectie met gekleurde achtergrond */}
                      <div className={`px-6 py-4 ${
                        activiteit.partnerToegestaan 
                          ? 'bg-gradient-to-r from-pink-600 to-purple-600' 
                          : 'bg-gradient-to-r from-indigo-600 to-blue-600'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <h3 className="text-xl font-semibold text-white">{activiteit.titel}</h3>
                          {activiteit.partnerToegestaan && (
                            <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Partners
                            </span>
                          )}
                          {(() => {
                            const status = getAanwezigheidStatus(activiteit.id);
                            if (!status) {
                              return (
                                <span className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium">
                                  Nog niet geregistreerd
                                </span>
                              );
                            }
                            if (status === 'niet') {
                              return (
                                <span className="bg-gray-500 text-white px-2 py-1 rounded text-xs font-medium">
                                  Niet aanwezig
                                </span>
                              );
                            }
                            if (status === 'alleen') {
                              return (
                                <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-medium">
                                  Aanwezig
                                </span>
                              );
                            }
                            if (status === 'met-partner') {
                              return (
                                <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium">
                                  Met partner
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>

                      {/* Content sectie met neutrale achtergrond */}
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-300 mb-4">
                          <div>
                            <span className="font-medium">Datum:</span> {formatDate(activiteit.datum)}
                          </div>
                          <div>
                            <span className="font-medium">Tijd:</span> {activiteit.tijd}
                          </div>
                          <div>
                            <span className="font-medium">Locatie:</span> {activiteit.locatie}
                          </div>
                        </div>
                        
                        {activiteit.beschrijving && (
                          <p className="text-gray-300 mb-4">{activiteit.beschrijving}</p>
                        )}
                        
                        {isPracLid && (
                          <div className="flex space-x-2 mb-4">
                            <button
                              onClick={() => handleEditActiviteit(activiteit)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                              title="Bewerken"
                            >
                              <FiEdit className="text-sm" />
                            </button>
                            <button
                              onClick={() => handleDeleteActiviteit(activiteit.id, activiteit.titel)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                              title="Verwijderen"
                            >
                              <FiTrash2 className="text-sm" />
                            </button>
                          </div>
                        )}

                        {/* Aanwezigheid Registratie */}
                        <div className="border-t border-white/20 pt-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-medium text-white">Aanwezigheid</h4>
                            <div className="flex items-center space-x-3">
                              <div className="text-sm text-gray-300">
                                {gereageerdCount} van {totaalLedenCount}
                              </div>
                              <button
                                onClick={() => setShowAanwezigheidDetails(showAanwezigheidDetails === activiteit.id ? null : activiteit.id)}
                                className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-medium transition-colors"
                              >
                                {showAanwezigheidDetails === activiteit.id ? 'Verberg' : 'Details'}
                              </button>
                            </div>
                          </div>
                          
                          {(() => {
                            const aanwezigheidStatus = getAanwezigheidStatus(activiteit.id);
                            const aanwezigheid = aanwezigheden.find(
                              a => a.activiteitId === activiteit.id && a.lidEmail === user?.email
                            );
                            
                            return (
                              <div className="bg-white/5 rounded-lg p-4">
                                {aanwezigheidStatus ? (
                                  <>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-white font-medium">Uw status:</span>
                                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                        aanwezigheidStatus === 'niet' 
                                          ? 'bg-red-100 text-red-800' 
                                          : 'bg-green-100 text-green-800'
                                      }`}>
                                        {aanwezigheidStatus === 'niet' && 'Niet aanwezig'}
                                        {aanwezigheidStatus === 'alleen' && 'Ja, ik kom alleen'}
                                        {aanwezigheidStatus === 'met-partner' && 'Ja, ik kom met partner'}
                                      </span>
                                    </div>
                                    
                                    <div className="flex space-x-2 mt-3">
                                      <button
                                        onClick={() => handleAanwezigheid(activiteit.id, 'alleen')}
                                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                          aanwezigheidStatus === 'alleen'
                                            ? 'bg-green-500 text-white'
                                            : 'bg-gray-500 text-white hover:bg-green-500'
                                        }`}
                                      >
                                        Ja, ik kom alleen
                                      </button>
                                      {activiteit.partnerToegestaan && (
                                        <button
                                          onClick={() => handleAanwezigheid(activiteit.id, 'met-partner')}
                                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                            aanwezigheidStatus === 'met-partner'
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-gray-500 text-white hover:bg-blue-500'
                                          }`}
                                        >
                                          Ja, ik kom met partner
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleAanwezigheid(activiteit.id, 'niet')}
                                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                          aanwezigheidStatus === 'niet'
                                            ? 'bg-red-500 text-white'
                                            : 'bg-gray-500 text-white hover:bg-red-500'
                                        }`}
                                      >
                                        Niet aanwezig
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-gray-300 mb-3">Geef aan of u aanwezig zult zijn:</p>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        onClick={() => handleAanwezigheid(activiteit.id, 'alleen')}
                                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium"
                                      >
                                        Ja, ik kom alleen
                                      </button>
                                      {activiteit.partnerToegestaan && (
                                        <button
                                          onClick={() => handleAanwezigheid(activiteit.id, 'met-partner')}
                                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
                                        >
                                          Ja, ik kom met partner
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleAanwezigheid(activiteit.id, 'niet')}
                                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
                                      >
                                        Niet aanwezig
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Aanwezigheid Details */}
                        {showAanwezigheidDetails === activiteit.id && (
                          <div className="border-t border-white/20 pt-4 mt-4">
                            <h5 className="text-lg font-medium text-white mb-4">Wie is aanwezig</h5>
                            {(() => {
                              const details = getAanwezigheidDetails(activiteit.id);
                              
                              return (
                                <div className="space-y-4">
                                  {/* Aanwezig Leden */}
                                  {details.aanwezigLeden.length > 0 && (
                                    <div>
                                      <h6 className="text-white font-medium mb-2 flex items-center">
                                        <span className="w-3 h-3 bg-green-400 rounded-full mr-2"></span>
                                        Aanwezig ({details.aanwezigLeden.length})
                                      </h6>
                                      <div className="bg-green-500/10 rounded-lg p-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {details.aanwezigLeden.map((aanwezigheid) => (
                                            <div key={aanwezigheid.id} className="text-green-300 text-sm">
                                              • {aanwezigheid.lidNaam}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Aanwezig met Partner */}
                                  {details.aanwezigMetPartner.length > 0 && (
                                    <div>
                                      <h6 className="text-white font-medium mb-2 flex items-center">
                                        <span className="w-3 h-3 bg-blue-400 rounded-full mr-2"></span>
                                        Aanwezig met partner ({details.aanwezigMetPartner.length})
                                      </h6>
                                      <div className="bg-blue-500/10 rounded-lg p-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {details.aanwezigMetPartner.map((aanwezigheid) => (
                                            <div key={aanwezigheid.id} className="text-blue-300 text-sm">
                                              • {aanwezigheid.lidNaam} + partner
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Niet Aanwezig */}
                                  {details.nietAanwezig.length > 0 && (
                                    <div>
                                      <h6 className="text-white font-medium mb-2 flex items-center">
                                        <span className="w-3 h-3 bg-red-400 rounded-full mr-2"></span>
                                        Niet aanwezig ({details.nietAanwezig.length})
                                      </h6>
                                      <div className="bg-red-500/10 rounded-lg p-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {details.nietAanwezig.map((aanwezigheid) => (
                                            <div key={aanwezigheid.id} className="text-red-300 text-sm">
                                              • {aanwezigheid.lidNaam}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Totaal Overzicht */}
                                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                                    <h6 className="text-white font-medium mb-3">Totaal Overzicht</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                                      <div>
                                        <div className="text-2xl font-bold text-green-400">{details.totaalLeden}</div>
                                        <div className="text-gray-300 text-sm">Leden</div>
                                      </div>
                                      <div>
                                        <div className="text-2xl font-bold text-blue-400">{details.totaalPartners}</div>
                                        <div className="text-gray-300 text-sm">Partners</div>
                                      </div>
                                      <div>
                                        <div className="text-2xl font-bold text-white">{details.totaalPersonen}</div>
                                        <div className="text-gray-300 text-sm">Totaal Personen</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default Prac; 